import { logEvent, setUserId } from "firebase/analytics";
import { analytics } from "../../firebase";
import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonButton,
  IonInput,
  IonSpinner,
  useIonRouter,
} from "@ionic/react";
import { useDispatch, useSelector } from "react-redux";
import { verifyEmail } from "../../redux/actions/currentProfileActions";
import { DEFAULT_HOME_PATH } from "../../utils/constents";
import { CanadianButton } from "../../ui-components/CanadianButton";
import axios from "axios";
import { SERVER_URL } from "../../api/client";
import "../Onboarding/Onboarding.css"; // Reuse styling
import { LandingHeader } from "../Slides/Landing";
import Footer from "../Slides/Footer";

const Verification = () => {
  const dispatch = useDispatch();

  const { data } = useSelector((state) => state.currentProfile);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [timer, setTimer] = useState(0);
  const router = useIonRouter();
  // Auto-submit when code reaches 6 digits
  useEffect(() => {
    if (code && code.length === 6) {
      handleVerify(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Cooldown timer effect
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleVerify = async (codeToVerify) => {
    // Determine the code to use: passed argument or state
    const currentCode = typeof codeToVerify === "string" ? codeToVerify : code;

    if (!currentCode || currentCode.length < 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const res = await dispatch(verifyEmail({ code: currentCode }));
      const response = res.payload;
      if (
        response &&
        (response.status === 200 || response.status === "active")
      ) {
        // Success
        if (analytics) {
          logEvent(analytics, "signup_completed");
          const userId = response?.data?._id || data?._id;
          if (userId) {
            setUserId(analytics, userId);
          }
        }
        // 2. Small delay to ensure Redux/Storage is settled
        // setTimeout(() => {
        //   // This is the "Nuclear Option" that works on all platforms
        //   // and solves the "Desktop needs a reload" issue.
        //   window.location.href = DEFAULT_HOME_PATH;
        //   // window.location.replace(DEFAULT_HOME_PATH);
        // }, 500);
        window.location.href = DEFAULT_HOME_PATH;
        // router.push(DEFAULT_HOME_PATH);
      } else {
        setError(
          response?.data?.errors?.[0]?.msg || "Invalid code. Please try again.",
        );
        // If verification failed, don't clear code automatically so user can edit it
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;

    setResendLoading(true);
    setResendMessage("");
    setError("");
    try {
      const email = data?.email;
      if (!email) {
        setError("User email not found. Please sign in again.");
        return;
      }
      await axios.post(`${SERVER_URL}/profile/resend-verification`, { email });
      setResendMessage("Code resent! Check your email.");
      setTimer(60);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear cookies/tokens and redirect
    document.cookie = "access_token=; Max-Age=0; path=/;";
    window.location.href = "/signin";
  };

  return (
    <IonPage>
      <LandingHeader />
      <IonContent fullscreen>
        <div
          className="onboarding-slide-content"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
            paddingTop: "60px",
          }}
        >
          <h3
            style={{
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            Verify Your Email
          </h3>
          <div
            style={{
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <p>We sent a 6-digit code to;</p>
            <p
              style={{
                fontWeight: "bold",
              }}
            >
              {data?.email}
            </p>
            <p>It expires in 15 minutes.</p>
          </div>

          <div className="onboarding-input-container">
            <IonInput
              placeholder="Enter 6-digit code"
              value={code}
              onIonChange={(e) => setCode(e.detail.value || "")}
              inputmode="numeric" // Forces numeric keypad on mobile
              // onIonInput={(e) => setCode(e.detail.value)}
              fill="outline"
              type="text"
              maxlength={6}
              style={{
                textAlign: "center",
                fontSize: "1.2rem",
              }}
            />
          </div>

          {error && (
            <div
              style={{
                color: "var(--ion-color-danger)",
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              {error}
            </div>
          )}
          {resendMessage && (
            <div
              style={{
                color: "var(--ion-color-success)",
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              {resendMessage}
            </div>
          )}

          <div className="onboarding-button-container">
            <CanadianButton
              expand="block"
              className="rounded-button poppins"
              onClick={() => handleVerify(code)}
              shape="round"
              color="success"
              disabled={loading}
            >
              {loading ? <IonSpinner name="dots" /> : "Verify"}
            </CanadianButton>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                margin: "20px 0",
              }}
            >
              <span>Didn't get the email?</span>
              <IonButton
                fill="clear"
                color="primary"
                onClick={handleResend}
                disabled={resendLoading || timer > 0}
              >
                {resendLoading ? (
                  <IonSpinner name="dots" />
                ) : timer > 0 ? (
                  `Resend in ${timer}s`
                ) : (
                  "Resend Code"
                )}
              </IonButton>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <span>Wrong email?</span>
              <IonButton fill="clear" color="primary" onClick={handleLogout}>
                Change
              </IonButton>
            </div>
          </div>
        </div>
        <Footer />
      </IonContent>
    </IonPage>
  );
};

export default Verification;
